/**
 * FeeAssumptionsTab Component
 * Settings page tab for managing fee assumption library
 * Phase 7 Batch 7.3.3 - Fee Assumptions Library
 *
 * Manages tenant-scoped fee assumption library (replaces localStorage).
 * Users can add, edit, and delete reusable assumptions.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pencil, Trash2, Plus, X, Check, AlertTriangle } from 'lucide-react';
import {
  useLibraryAssumptions,
  useCreateAssumption,
  useUpdateAssumption,
  useDeleteAssumption,
  type FeeAssumption,
} from '@/hooks/useFeeAssumptions';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function FeeAssumptionsTab() {
  const { data, isLoading } = useLibraryAssumptions();
  const createMutation = useCreateAssumption();
  const updateMutation = useUpdateAssumption();
  const deleteMutation = useDeleteAssumption();

  const [newAssumptionText, setNewAssumptionText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const assumptions = data?.fee_assumptions || [];

  const handleAdd = () => {
    if (!newAssumptionText.trim()) return;

    createMutation.mutate(newAssumptionText.trim(), {
      onSuccess: () => {
        setNewAssumptionText('');
      },
    });
  };

  const handleStartEdit = (assumption: FeeAssumption) => {
    setEditingId(assumption.id);
    setEditText(assumption.assumption_text);
  };

  const handleSaveEdit = () => {
    if (!editingId || !editText.trim()) return;

    updateMutation.mutate(
      { id: editingId, assumptionText: editText.trim() },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditText('');
        },
      }
    );
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleDelete = (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmId) {
      deleteMutation.mutate(deleteConfirmId);
    }
    setDeleteConfirmId(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmId(null);
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="space-y-1">
        <h2 className="text-2xl font-bold tracking-tight">Fee Assumptions Library</h2>
        <p className="text-muted-foreground">
          Manage reusable fee assumptions for quick-add when creating proposals and projects.
        </p>
      </div>

      {/* Help Text */}
      <div className="rounded-lg border bg-muted/50 p-4">
        <p className="text-sm text-muted-foreground mb-2">
          Create a library of commonly-used fee assumptions. When creating projects, you can
          quickly add these assumptions from a searchable dropdown.
        </p>
        <p className="text-sm text-muted-foreground">
          <strong>Example:</strong> "Based on 500 sq ft addition" or "Standard residential
          construction rates"
        </p>
      </div>

      {/* Assumptions List */}
      <div className="space-y-4">
        <Label>Library Assumptions ({assumptions.length})</Label>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : assumptions.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground">
              No fee assumptions yet. Add your first one below.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {assumptions.map((assumption) => (
              <div
                key={assumption.id}
                className="flex items-center gap-2 rounded-md border p-3 hover:bg-muted/50"
              >
                {editingId === assumption.id ? (
                  // Edit mode
                  <>
                    <Input
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="flex-1"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveEdit();
                        } else if (e.key === 'Escape') {
                          handleCancelEdit();
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleSaveEdit}
                      disabled={updateMutation.isPending}
                      aria-label="Save"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleCancelEdit}
                      disabled={updateMutation.isPending}
                      aria-label="Cancel"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  // Display mode
                  <>
                    <p className="flex-1 text-sm">{assumption.assumption_text}</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleStartEdit(assumption)}
                      aria-label="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(assumption.id)}
                      disabled={deleteMutation.isPending}
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add New Assumption */}
      <div className="space-y-2">
        <Label htmlFor="new-assumption">Add New Assumption</Label>
        <div className="flex gap-2">
          <Input
            id="new-assumption"
            value={newAssumptionText}
            onChange={(e) => setNewAssumptionText(e.target.value)}
            placeholder="Enter a new fee assumption..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAdd();
              }
            }}
          />
          <Button
            onClick={handleAdd}
            disabled={!newAssumptionText.trim() || createMutation.isPending}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && handleCancelDelete()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Fee Assumption
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this fee assumption? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
